import { useCallback, useState } from 'react';
import {
  IHSANROLL_OFFICERS,
  IHSANROLL_PROGRAMME,
  IHSANROLL_STORAGE_KEY
} from '../data/ihsanRollMock';
import { sgDateISO, sgTimeHHMM } from './dates';

const pad = (n) => String(n).padStart(2, '0');

// Dates are pinned to Asia/Singapore rather than the device clock — see
// lib/dates.js. These re-exports keep the existing call sites unchanged.
export const todayISO = sgDateISO;
export const nowHHMM = sgTimeHHMM;

export function formatTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${pad(m || 0)} ${ampm}`;
}

export function formatDateLabel(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-SG', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

export function attendanceKey(caseNo, date = todayISO()) {
  return `${caseNo}:${date}`;
}

function atLocal(date, hhmm) {
  const [h, m] = String(hhmm || '00:00').split(':').map(Number);
  const next = new Date(date);
  next.setHours(h || 0, m || 0, 0, 0);
  return next;
}

export function expectedReturnAt(outEntry) {
  const created = outEntry.createdAt ? new Date(outEntry.createdAt) : new Date();
  const outAt = atLocal(created, outEntry.time);
  const expected = atLocal(created, outEntry.expectedReturn);
  if (expected <= outAt) expected.setDate(expected.getDate() + 1);
  return expected;
}

export function isOverdue(outEntry, now = new Date()) {
  if (!outEntry || outEntry.type !== 'out' || !outEntry.expectedReturn) return false;
  return now > expectedReturnAt(outEntry);
}

export function overdueMs(outEntry, now = new Date()) {
  return Math.max(0, now - expectedReturnAt(outEntry));
}

export function formatOverdue(ms) {
  const mins = Math.max(0, Math.round(ms / 60000));
  if (mins < 60) return `${mins} min overdue`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m overdue` : `${h}h overdue`;
}

export function openOutFor(store, caseNo) {
  const outs = (store.movement || []).filter((e) => e.caseNo === caseNo && e.type === 'out');
  const closed = new Set(
    (store.movement || []).filter((e) => e.type === 'in' && e.closes).map((e) => e.closes)
  );
  const open = outs.filter((e) => !closed.has(e.id));
  return open[open.length - 1] || null;
}

export function tileStatus(store, caseNo, now = new Date()) {
  const open = openOutFor(store, caseNo);
  if (!open) return 'in';
  return isOverdue(open, now) ? 'overdue' : 'out';
}

export function hasAttendance(store, caseNo, date = todayISO()) {
  const sheet = store.attendance?.[attendanceKey(caseNo, date)];
  if (!sheet?.items) return false;
  return Object.values(sheet.items).some((item) => item.status);
}

export function attendanceCompleteness(store, caseNo, date) {
  const sheet = store.attendance?.[attendanceKey(caseNo, date)];
  if (!sheet?.items) return 'none';
  const statuses = IHSANROLL_PROGRAMME.map((p) => sheet.items[p.id]?.status);
  if (statuses.every((s) => s === 'attended' || s === 'absent')) return 'logged';
  if (statuses.some(Boolean)) return 'partial';
  return 'none';
}

export function blankSheet() {
  return IHSANROLL_PROGRAMME.reduce((acc, item) => {
    acc[item.id] = { status: null, remarks: '' };
    return acc;
  }, {});
}

export function last7Days(from = new Date()) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(from);
    d.setDate(from.getDate() - (6 - i));
    return todayISO(d);
  });
}

export function movementsOnDate(store, caseNo, date) {
  return (store.movement || [])
    .filter((e) => e.caseNo === caseNo && todayISO(new Date(e.createdAt)) === date)
    .sort((a, b) => String(a.time).localeCompare(String(b.time)));
}

export function editsFor(store, targetPrefix) {
  return (store.edits || []).filter((e) => e.target.startsWith(targetPrefix));
}

function emptyStore() {
  return { dutyOfficer: null, attendance: {}, movement: [], edits: [], seeded: false };
}

function seedStore(store) {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  yesterday.setHours(16, 5, 0, 0);
  const evening = new Date(now);
  evening.setHours(19, 0, 0, 0);

  const allAttended = IHSANROLL_PROGRAMME.reduce((acc, item) => {
    acc[item.id] = { status: 'attended', remarks: '' };
    return acc;
  }, {});
  const partial = blankSheet();
  partial.subuh = { status: 'attended', remarks: '' };
  partial.breakfast = { status: 'attended', remarks: '' };
  partial.school = { status: 'absent', remarks: 'Clinic' };

  return {
    ...store,
    seeded: true,
    attendance: {
      [attendanceKey(3, todayISO(now))]: {
        items: allAttended,
        savedBy: IHSANROLL_OFFICERS[0],
        savedAt: new Date(now.getTime() - 3600000).toISOString()
      },
      [attendanceKey(12, todayISO(now))]: {
        items: partial,
        savedBy: IHSANROLL_OFFICERS[1],
        savedAt: new Date(now.getTime() - 7200000).toISOString()
      }
    },
    movement: [
      {
        id: 'seed-out-19',
        caseNo: 19,
        type: 'out',
        time: '16:00',
        place: 'Hospital — SGH A&E',
        expectedReturn: '18:00',
        authorisedBy: IHSANROLL_OFFICERS[1],
        officer: IHSANROLL_OFFICERS[0],
        createdAt: yesterday.toISOString()
      },
      {
        id: 'seed-out-7',
        caseNo: 7,
        type: 'out',
        time: '19:00',
        place: 'Family visit — aunt',
        expectedReturn: '23:45',
        authorisedBy: IHSANROLL_OFFICERS[2],
        officer: IHSANROLL_OFFICERS[0],
        createdAt: evening.toISOString()
      }
    ]
  };
}

function normalize(raw) {
  const base = emptyStore();
  if (!raw || typeof raw !== 'object') return seedStore(base);
  return {
    dutyOfficer: raw.dutyOfficer || null,
    attendance: raw.attendance && typeof raw.attendance === 'object' ? raw.attendance : {},
    movement: Array.isArray(raw.movement) ? raw.movement : [],
    edits: Array.isArray(raw.edits) ? raw.edits : [],
    seeded: Boolean(raw.seeded)
  };
}

export function loadStore() {
  try {
    const raw = localStorage.getItem(IHSANROLL_STORAGE_KEY);
    if (raw) {
      const parsed = normalize(JSON.parse(raw));
      if (!parsed.seeded && !parsed.movement.length && !Object.keys(parsed.attendance).length) {
        const seeded = seedStore(parsed);
        saveStore(seeded);
        return seeded;
      }
      return parsed;
    }
  } catch {
    /* private mode — in-memory only */
  }
  const seeded = seedStore(emptyStore());
  saveStore(seeded);
  return seeded;
}

export function saveStore(store) {
  try {
    localStorage.setItem(IHSANROLL_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* private mode */
  }
  return store;
}

export function setDutyOfficer(store, name) {
  return { ...store, dutyOfficer: name };
}

export function saveAttendance(store, caseNo, date, items, officer) {
  const key = attendanceKey(caseNo, date);
  const prev = store.attendance[key];
  const savedAt = new Date().toISOString();
  const edits = [...(store.edits || [])];
  if (prev?.items) {
    IHSANROLL_PROGRAMME.forEach((p) => {
      const before = prev.items[p.id] || { status: null, remarks: '' };
      const after = items[p.id] || { status: null, remarks: '' };
      if (before.status !== after.status) {
        edits.push({
          target: `attendance:${key}:${p.id}`,
          field: 'status',
          from: before.status,
          to: after.status,
          by: officer,
          at: savedAt
        });
      }
      if ((before.remarks || '') !== (after.remarks || '')) {
        edits.push({
          target: `attendance:${key}:${p.id}`,
          field: 'remarks',
          from: before.remarks || '',
          to: after.remarks || '',
          by: officer,
          at: savedAt
        });
      }
    });
  }
  return {
    ...store,
    edits,
    attendance: {
      ...store.attendance,
      [key]: { items, savedBy: officer, savedAt }
    }
  };
}

export function addMovement(store, entry) {
  return { ...store, movement: [...(store.movement || []), entry] };
}

export function editMovement(store, id, field, value, officer) {
  const savedAt = new Date().toISOString();
  const current = (store.movement || []).find((e) => e.id === id);
  if (!current) return store;
  const from = current[field];
  if (from === value) return store;
  return {
    ...store,
    edits: [
      ...(store.edits || []),
      { target: `movement:${id}`, field, from, to: value, by: officer, at: savedAt }
    ],
    movement: store.movement.map((e) => (e.id === id ? { ...e, [field]: value } : e))
  };
}

export function newMovementId() {
  return `mv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function useIhsanRoll() {
  const [store, setStore] = useState(() => loadStore());
  const update = useCallback((mutator) => {
    setStore((prev) => {
      const next = typeof mutator === 'function' ? mutator(prev) : mutator;
      saveStore(next);
      return next;
    });
  }, []);
  return [store, update];
}

export function whereabouts(store, now = new Date()) {
  const open = (store.movement || [])
    .filter((e) => e.type === 'out' && openOutFor(store, e.caseNo)?.id === e.id)
    .map((entry) => ({
      ...entry,
      overdue: isOverdue(entry, now),
      overdueMs: isOverdue(entry, now) ? overdueMs(entry, now) : 0
    }));
  open.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    return b.overdueMs - a.overdueMs;
  });
  const overdue = open.filter((e) => e.overdue).length;
  const out = open.length;
  return { open, out, overdue, inCount: 40 - out };
}
